import { useCallback, useEffect, useRef, useState } from 'react'
import {
  FileSignature,
  Download,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  ExternalLink,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../services/api'
import type { DocumentSignature, DocumentSignatureStatus } from '../types'

const STATUS_CONFIG: Record<
  DocumentSignatureStatus,
  { label: string; color: string; icon: React.ElementType }
> = {
  pending: {
    label: 'En attente',
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    icon: Clock,
  },
  signed: {
    label: 'Signé',
    color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    icon: CheckCircle,
  },
  refused: {
    label: 'Refusé',
    color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    icon: XCircle,
  },
  expired: {
    label: 'Expiré',
    color: 'bg-gray-100 text-gray-700 dark:bg-gray-700/50 dark:text-gray-300',
    icon: AlertCircle,
  },
}

function StatusBadge({ status }: { status: DocumentSignatureStatus }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.pending
  const Icon = cfg.icon
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  )
}

export default function DocumentSignaturesPage() {
  useTranslation()
  const [records, setRecords] = useState<DocumentSignature[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [downloadingId, setDownloadingId] = useState<number | null>(null)
  const [offset, setOffset] = useState(0)
  const limit = 20
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const load = useCallback(
    async (currentOffset = 0) => {
      setLoading(true)
      try {
        const res = await api.getDocumentSignatures({ limit, offset: currentOffset })
        setRecords(res.data)
        setTotal(res.total)
      } catch {
        setError('Impossible de charger les documents.')
      } finally {
        setLoading(false)
      }
    },
    [limit]
  )

  useEffect(() => {
    load(offset)
  }, [load, offset])

  useEffect(() => {
    const hasPending = records.some((r) => r.status === 'pending')
    if (!hasPending) {
      if (pollRef.current) clearInterval(pollRef.current)
      return
    }
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.getDocumentSignatures({ limit, offset })
        setRecords(res.data)
        setTotal(res.total)
      } catch {
        // silent
      }
    }, 30_000)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [records, limit, offset])

  const handleDownload = async (rec: DocumentSignature) => {
    setDownloadingId(rec.id)
    try {
      const res = await api.downloadDocumentSignature(rec.id)
      const link = document.createElement('a')
      link.href = `data:application/pdf;base64,${res.pdf}`
      link.download = `${res.document_name ?? rec.document_name}-signé.pdf`
      link.click()
    } catch {
      alert("Le document signé n'est pas encore disponible.")
    } finally {
      setDownloadingId(null)
    }
  }

  const formatDate = (iso: string | null) => {
    if (!iso) return '—'
    return new Date(iso + (iso.endsWith('Z') ? '' : 'Z')).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <FileSignature className="w-6 h-6 text-blue-600" />
          Signatures de documents
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Documents envoyés pour votre signature électronique.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : records.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400 dark:text-gray-500">
            <FileSignature className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-sm">Aucun document en attente de signature.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Document</th>
                  <th className="px-4 py-3 text-left">Statut</th>
                  <th className="px-4 py-3 text-left">Envoyé le</th>
                  <th className="px-4 py-3 text-left">Signé le</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {records.map((rec) => (
                  <tr
                    key={rec.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                  >
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                      {rec.document_name}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={rec.status} />
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                      {formatDate(rec.created_at)}
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                      {formatDate(rec.signed_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {rec.status === 'pending' && rec.signing_url && (
                          <a
                            href={rec.signing_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            Signer
                          </a>
                        )}
                        {rec.status === 'signed' && (
                          <button
                            onClick={() => handleDownload(rec)}
                            disabled={downloadingId === rec.id}
                            className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {downloadingId === rec.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Download className="w-3.5 h-3.5" />
                            )}
                            Télécharger
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {total > limit && (
        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <span>
            {offset + 1}–{Math.min(offset + limit, total)} sur {total}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={offset === 0}
              className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Précédent
            </button>
            <button
              onClick={() => setOffset(offset + limit)}
              disabled={offset + limit >= total}
              className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Suivant
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
